import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const clearSnapshots = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("opm_snap"))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      const prev = localStorage.getItem("opm_snap_owner");
      const al = res.data.allowed_locations;
      const owner = `${res.data.email}:${Array.isArray(al) ? al.join("+") : "all"}`;
      if (prev && prev !== owner) clearSnapshots();
      try { localStorage.setItem("opm_snap_owner", owner); } catch {}
      setUser(res.data);
    } catch {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    clearSnapshots();
    try { localStorage.removeItem("opm_snap_owner"); } catch {}
    setUser(null);
    window.location.href = "/login";
  };

  return <AuthContext.Provider value={{ user, setUser, loading, logout }}>{children}</AuthContext.Provider>;
}

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFDFC]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-300 border-t-[#D4AF37]" data-testid="auth-loading" />
      </div>
    );
  if (!user) return null;
  return children;
}
